import React from 'react'

const W = 880
const H = 300
const M = { left: 44, right: 16, top: 18, bottom: 52 }

/**
 * Programs run per quarter against the break-even threshold. The threshold is
 * drawn as a step function because it is genuinely lower in summer, when camps
 * raise contribution per program. Seasons still in registration are drawn
 * dashed and are not scored against the line.
 */
function BreakEvenChart({ seasons, breakEvenSchoolYear, breakEvenSummer }) {
  const innerW = W - M.left - M.right
  const innerH = H - M.top - M.bottom

  const values = seasons.map((s) => (s.ran === null ? s.offered : s.ran))
  const maxValue = Math.max(
    60,
    ...values,
    breakEvenSchoolYear || 0,
    breakEvenSummer || 0
  ) * 1.12

  const y = (v) => M.top + innerH - (v / maxValue) * innerH
  const band = innerW / Math.max(seasons.length, 1)
  const barW = Math.min(58, band * 0.52)

  const thresholdFor = (s) => (s.summer ? breakEvenSummer : breakEvenSchoolYear)

  const gridLines = [0, 1, 2, 3, 4].map((i) => {
    const v = (maxValue / 4) * i
    return (
      <g key={`g${i}`}>
        <line x1={M.left} y1={y(v)} x2={W - M.right} y2={y(v)} stroke="var(--be-line)" strokeWidth="1" />
        <text x={M.left - 9} y={y(v) + 4} textAnchor="end" className="be-axis">{Math.round(v)}</text>
      </g>
    )
  })

  const bars = seasons.map((s, i) => {
    const cx = M.left + band * i + band / 2
    const threshold = thresholdFor(s)
    const pending = s.ran === null
    const v = pending ? s.offered : s.ran
    const cleared = !pending && threshold !== null && v >= threshold
    const fill = pending ? 'var(--be-pending)' : cleared ? 'var(--be-good)' : 'var(--be-short)'
    const top = y(v)
    const height = Math.max(2, M.top + innerH - top)

    return (
      <g key={s.key}>
        <rect
          className="be-bar"
          x={cx - barW / 2}
          y={top}
          width={barW}
          height={height}
          rx="4"
          ry="4"
          fill={fill}
          fillOpacity={pending ? 0.42 : 1}
          stroke={pending ? 'var(--be-pending)' : undefined}
          strokeWidth={pending ? 1.5 : undefined}
          strokeDasharray={pending ? '4 3' : undefined}
        >
          <title>
            {`${s.label} ${s.yearShort} — ${pending
              ? `${s.offered} offered, still in registration`
              : `${s.ran} ran of ${s.offered} offered`}`}
            {threshold !== null ? ` · break-even ${threshold}` : ''}
          </title>
        </rect>
        <text x={cx} y={top - 8} textAnchor="middle" className={`be-barval${pending ? ' be-muted' : ''}`}>
          {v}{pending ? '*' : ''}
        </text>
        <text x={cx} y={H - M.bottom + 20} textAnchor="middle" className="be-xlab">{s.label}</text>
        <text x={cx} y={H - M.bottom + 34} textAnchor="middle" className="be-xyr">{s.yearShort}</text>
      </g>
    )
  })

  let path = ''
  seasons.forEach((s, i) => {
    const threshold = thresholdFor(s)
    if (threshold === null || threshold === undefined) return
    const x0 = M.left + band * i
    const x1 = x0 + band
    path += `${path === '' ? 'M' : 'L'}${x0},${y(threshold)} L${x1},${y(threshold)}`
  })

  const labelValue = breakEvenSchoolYear

  return (
    <div className="be-chart-wrap">
      <svg
        width={W}
        height={H}
        role="img"
        aria-label="Programs run per quarter against the break-even threshold"
      >
        {gridLines}
        {bars}
        {path && (
          <path d={path} fill="none" stroke="var(--be-ink)" strokeWidth="2" strokeLinejoin="round" />
        )}
        {labelValue !== null && labelValue !== undefined && (
          // Anchored left: the rightmost bars are the tallest and the most
          // likely to be a season still in registration, so a right-hand
          // label would sit on top of them.
          <g>
            <rect
              x={M.left + 6} y={y(labelValue) - 24} width="112" height="18" rx="4"
              fill="var(--be-surface)" stroke="var(--be-line)"
            />
            <text x={M.left + 62} y={y(labelValue) - 11} textAnchor="middle" className="be-linelab">
              break-even {labelValue}
            </text>
          </g>
        )}
      </svg>
    </div>
  )
}

export default BreakEvenChart
